<template>
  <div class="showcase-page">
    <div class="showcase-inner">
      <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
      <header class="showcase-hero">
        <h1>Showcase pieces</h1>
        <p class="pieces-lede">
          Solos and small groups for the showcase — optional.
          Up to <strong>3 solos</strong>, plus any small groups you’re in
          (<strong>5&nbsp;½ minutes</strong> total on stage; each piece under <strong>3:15</strong>).
          Competition is also optional — one judged piece max.
        </p>
      </header>

      <input
        ref="fileInput"
        type="file"
        accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav"
        style="display:none"
        @change="onFileChosen"
      >

      <!-- ═══ HOW IT WORKS ═══ -->
      <section class="hub-section">
        <div class="hub-panel iabf-card" :class="{ 'is-expanded': howItWorksOpen }">
          <button type="button" class="hub-panel-toggle" @click="howItWorksOpen = !howItWorksOpen">
            <div>
              <span class="hub-panel-kicker">Guide</span>
              <h2 class="hub-panel-title">How it works</h2>
              <p class="hub-panel-summary">Stage time, music licensing, costumes, and tips.</p>
            </div>
            <span class="slot-chev">{{ howItWorksOpen ? '▴' : '▾' }}</span>
          </button>
          <div v-show="howItWorksOpen" class="hub-panel-body how-body">
            <h3 class="how-h">Solos &amp; small groups</h3>
            <p>
              All levels are welcome — yes, beginners too. Your piece may be a classical variation,
              original choreography, contemporary or lyrical, en pointe or not.
            </p>
            <p>
              Each dancer has <strong>5 ½ minutes on stage</strong> total, across up to
              <strong>3 solos</strong> plus small groups. Each piece must be under <strong>3:15</strong>.
            </p>
            <p>
              Small groups share the music. A 3-minute trio counts as 1 minute for each
              of you toward your 5½ minutes. Groups don’t count toward those 3 solos.
            </p>
            <p>
              Need a teacher or choreography help? We’ll gladly connect you with someone who can
              rehearse and prepare your piece remotely.
            </p>

            <h3 class="how-h">Classical variations</h3>
            <p>
              Classical variations are first-come, first served.
              <button type="button" class="inline-link-btn" @click="openLineupSearch">
                Search the showcase lineup
              </button>
              to check if a variation is still available.
            </p>

            <h3 class="how-h">Pop music</h3>
            <p>
              Pop or licensed music is subject to availability.
              Please <a href="https://www.ascap.com/repertory" target="_blank" rel="noopener">search ASCAP</a>
              before registering.
            </p>
            <p class="how-video-link">
              <a
                href="https://cdn.jwplayer.com/players/PAvHMMzj-ZbyADQFv.html"
                target="_blank"
                rel="noopener"
              >▶ Watch: how to look up music in the ASCAP database</a>
              <span class="how-video-meta"> (~3 min)</span>
            </p>

            <h3 class="how-h">Music files</h3>
            <p>
              Please provide your music files, trimmed to under <strong>3:15</strong>.
              <strong>WAV, MP3, or M4A</strong> accepted.
            </p>

            <h3 class="how-h">Costumes</h3>
            <p>
              Wear simple class clothes, buy a full costume, or re-use pieces loaned through IABF corps
              de ballet works. Shops that fit adult dancers:
            </p>
            <ul>
              <li>Bluebird Dancewear</li>
              <li>Laylas Costumes</li>
              <li>Maria's Professional Ballet Costume Design</li>
              <li>Benefis Shop</li>
              <li>Revolution Dancewear</li>
              <li>Kelle</li>
              <li>Weissmans</li>
            </ul>
            <p class="how-note">
              Studio accounts available for Kelle, Weissmans, and Revolution Dancewear.
              Email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> for quotes and orders.
            </p>
          </div>
        </div>
      </section>

      <!-- ═══ YOUR PIECES ═══ -->
      <section class="hub-section">
        <div class="section-head">
          <h2>Your pieces</h2>
          <p v-if="!isNotPerformingSolos" class="section-lede">
            Music uploads and program details live on each piece card.
          </p>
        </div>

        <!-- Only for empty / not-performing — done state lives next to “Add another” -->
        <div v-if="showShowcasePrepCard" class="showcase-prep-card iabf-card">
          <template v-if="isNotPerformingSolos">
            <p class="showcase-prep-status">
              <strong>Not performing solos</strong> — you’re all set on this.
            </p>
            <p class="showcase-prep-hint">
              Changed your mind? Clear this choice to add a piece or explore competition.
            </p>
            <button
              type="button"
              class="piece-link-btn"
              :disabled="showcasePrepSaving"
              @click="setShowcasePrep(null)"
            >
              Clear “not performing”
            </button>
          </template>

          <template v-else-if="!rows.length">
            <p class="showcase-prep-status">
              Solos are <strong>optional</strong>. Add a piece when you’re ready — or tell us you’re
              not performing so this stays off your to-do list.
            </p>
            <div class="showcase-prep-actions">
              <button
                type="button"
                class="iabf-btn iabf-btn--secondary"
                :disabled="showcasePrepSaving || registrationClosed"
                @click="setShowcasePrep('not_performing')"
              >
                {{ showcasePrepSaving ? 'Saving…' : 'Not performing solos' }}
              </button>
            </div>
          </template>
          <p v-if="showcasePrepError" class="form-error" role="alert">{{ showcasePrepError }}</p>
        </div>

        <div v-if="!isNotPerformingSolos" class="piece-slots">
          <!-- Registered pieces only (no empty placeholders for 2 & 3) -->
          <article
            v-for="(piece, idx) in rows"
            :key="piece.id"
            class="piece-slot iabf-card"
            :class="{ 'is-open': openPieceId === piece.id }"
          >
              <button
                type="button"
                class="slot-header"
                :aria-expanded="openPieceId === piece.id ? 'true' : 'false'"
                @click="togglePieceOpen(piece.id)"
              >
                <div class="slot-header-main">
                  <span class="slot-badge">Piece {{ idx + 1 }}</span>
                  <div>
                    <h3 class="piece-card-title">{{ piece.title || '(untitled)' }}</h3>
                    <p v-if="piece.songArtist" class="piece-card-artist">{{ piece.songArtist }}</p>
                  </div>
                </div>
                <div class="slot-header-meta">
                  <span
                    v-if="isPiecePending(piece)"
                    class="meta-chip is-pending"
                  >Pending approval</span>
                  <span
                    v-if="piece.id === competitionPieceId"
                    class="comp-pill"
                  >🏅 Competition</span>
                  <span
                    v-if="piece.musicReceived"
                    class="meta-chip is-ok"
                  >Music ✓</span>
                  <span
                    v-else-if="!isPiecePending(piece)"
                    class="meta-chip is-warn"
                  >Music needed</span>
                  <span class="slot-chev">{{ openPieceId === piece.id ? '▴' : '▾' }}</span>
                </div>
              </button>

              <div v-show="openPieceId === piece.id" class="slot-body">
                <div v-if="isPiecePending(piece)" class="pending-note">
                  Submitted — the IABF team will review and approve this piece for the showcase lineup.
                  After it’s approved, you’ll be able to upload music and share what the solo means to you.
                </div>
                <div class="piece-card-meta">
                  <span v-if="piece.pieceType">{{ piece.pieceType }}</span>
                  <span v-if="piece.groupSize">{{ piece.groupSize }}</span>
                  <span class="piece-length">
                    {{ pieceTimeLabel(piece) }}
                  </span>
                </div>
                <p class="piece-card-program">
                  <strong>Program:</strong>
                  {{ piece.programTitle || ((piece.title || '') + (piece.songArtist ? ' — ' + piece.songArtist : '')) }}
                  <span v-if="piece.programDancer || listDancerNames(piece.dancers)">
                    · {{ piece.programDancer || listDancerNames(piece.dancers) }}
                  </span>
                  <span v-if="piece.programTeacher">
                    · Choreographed/coached by {{ piece.programTeacher }}
                  </span>
                  <button
                    v-if="!isPiecePending(piece)"
                    type="button"
                    class="piece-link-btn piece-program-edit"
                    @click="setEditedPieceId(piece)"
                  >edit</button>
                </p>
                <div class="piece-card-actions">
                  <div v-if="!isPiecePending(piece)" class="piece-music">
                    <span v-if="uploadingPieceId === piece.id" class="piece-music-status">
                      <v-progress-circular indeterminate size="18" width="2" /> Uploading…
                    </span>
                    <div v-else-if="piece.musicReceived" class="piece-music-status is-ok">
                      <div class="piece-music-row">
                        <span>Music received</span>
                        <button type="button" class="piece-link-btn" @click="pickFile(piece)">Replace</button>
                      </div>
                      <p v-if="piece.musicFileName" class="music-file-name">{{ piece.musicFileName }}</p>
                      <audio
                        v-if="piece.musicUrl"
                        class="piece-music-player"
                        :src="piece.musicUrl"
                        controls
                        preload="metadata"
                        controlsList="nodownload"
                        @play="onMusicPreviewPlay(piece.id, $event)"
                      >
                        Your browser can’t play this audio file.
                      </audio>
                    </div>
                    <button
                      v-else
                      type="button"
                      class="iabf-btn iabf-btn--secondary piece-upload-btn"
                      @click="pickFile(piece)"
                    >
                      Upload mp3
                    </button>
                    <div v-if="uploadError[piece.id]" class="upload-error">{{ uploadError[piece.id] }}</div>
                  </div>
                  <div class="piece-comp">
                    <template v-if="piece.id === competitionPieceId && piece.competition && piece.competition !== 'None'">
                      <span class="comp-pill">🏅 {{ piece.competition }}</span>
                    </template>
                    <template v-else-if="piece.id === competitionPieceId">
                      <span class="comp-pill comp-pill--pending">Competition — pending division</span>
                    </template>
                    <template v-else-if="piece.competition && piece.competition !== 'None'">
                      <span class="comp-pill">{{ piece.competition }}</span>
                    </template>
                  </div>
                </div>

                <div v-if="!isPiecePending(piece)" class="meaning-block">
                  <p class="meaning-label">What this solo means to me</p>
                  <p class="meaning-hint">Optional — shared with other dancers in a special program at IABF.</p>
                  <textarea
                    class="meaning-input"
                    rows="3"
                    maxlength="600"
                    :value="meaningDraft[piece.id] != null ? meaningDraft[piece.id] : (piece.meaningToMe || '')"
                    placeholder="Why this piece, this year…"
                    @input="onMeaningInput(piece.id, $event)"
                  />
                  <div class="meaning-actions">
                    <button
                      type="button"
                      class="iabf-btn iabf-btn--secondary"
                      :disabled="meaningSavingId === piece.id || !meaningDirty[piece.id]"
                      @click="saveMeaning(piece)"
                    >
                      {{ meaningSavingId === piece.id ? 'Saving…' : 'Save story' }}
                    </button>
                    <span v-if="meaningSuccess[piece.id]" class="meaning-ok">Saved</span>
                    <span v-if="meaningError[piece.id]" class="upload-error">{{ meaningError[piece.id] }}</span>
                  </div>
                </div>

                <div class="piece-danger">
                  <button
                    type="button"
                    class="piece-delete-btn"
                    :disabled="cancelingPieceId === piece.id"
                    @click="confirmCancelPiece(piece)"
                  >
                    {{ cancelingPieceId === piece.id ? 'Removing…' : 'Remove this piece' }}
                  </button>
                  <p v-if="cancelError[piece.id]" class="upload-error">{{ cancelError[piece.id] }}</p>
                </div>
              </div>
          </article>

          <!-- No pieces yet: one clear CTA (not three empty slots) -->
          <div v-if="!rows.length" class="slot-empty iabf-card">
            <h3 class="slot-empty-title">No showcase pieces yet</h3>
            <p class="slot-empty-sub">
              {{ registrationClosed
                ? 'Registration is closed — email hello@iabf.dance to add a piece.'
                : 'Add a solo or small group whenever you’re ready — totally optional.' }}
            </p>
            <button
              v-if="!registrationClosed"
              type="button"
              class="iabf-btn iabf-btn--primary slot-add-btn"
              @click="startAddPiece"
            >
              Add a piece
            </button>
          </div>

          <!-- Pieces registered: add another (+ I’m done until they mark pieces finished) -->
          <div v-else class="add-another-row">
            <button
              v-if="!registrationClosed"
              type="button"
              class="iabf-btn iabf-btn--secondary slot-add-btn"
              :disabled="showcasePrepSaving"
              @click="onAddAnotherPiece"
            >
              Add another piece
            </button>
            <button
              v-if="!isShowcasePiecesDone && canMarkShowcaseAllSet"
              type="button"
              class="iabf-btn iabf-btn--primary slot-add-btn"
              :disabled="showcasePrepSaving"
              @click="setShowcasePrep('all_set')"
            >
              {{ showcasePrepSaving ? 'Saving…' : 'I’m done' }}
            </button>
            <p v-if="showcasePrepError" class="form-error" role="alert">{{ showcasePrepError }}</p>
          </div>
        </div>
      </section>

      <!-- ═══ COMPETITION ═══ -->
      <section v-if="!isNotPerformingSolos" class="hub-section">
        <div
          class="hub-panel iabf-card"
          :class="{ 'is-expanded': competitionOpen }"
        >
          <button type="button" class="hub-panel-toggle" @click="competitionOpen = !competitionOpen">
            <div>
              <span class="hub-panel-kicker">Optional</span>
              <h2 class="hub-panel-title">Competition</h2>
              <p class="hub-panel-summary">{{ competitionSummary }}</p>
            </div>
            <span class="slot-chev">{{ competitionOpen ? '▴' : '▾' }}</span>
          </button>

          <div v-show="competitionOpen" class="hub-panel-body">
            <p class="comp-intro">
              You may enter <strong>one</strong> piece for judging and written feedback.
              Extra pieces still perform in the showcase — they just aren’t scored.
            </p>

            <details class="comp-about-details">
              <summary>About the IABF competition</summary>
              <competition-about />
            </details>

            <div v-if="!approvedRows.length" class="comp-empty">
              <template v-if="rows.length && !approvedRows.length">
                Your piece is still pending approval from the IABF team. Once it’s approved, you can enter competition.
              </template>
              <template v-else>
                Register a showcase piece first, then come back to choose your competition entry.
                <button type="button" class="piece-link-btn" @click="startAddPiece">Add a piece →</button>
              </template>
            </div>

            <template v-else-if="isCompetitionRegistered">
              <div class="comp-registered">
                <div class="comp-registered-badge">You’re registered to compete</div>
                <div v-if="competitionPiece" class="comp-piece-line">
                  <strong>{{ competitionPiece.title }}</strong>
                  <span v-if="competitionPiece.songArtist"> — {{ competitionPiece.songArtist }}</span>
                  <span v-if="competitionPiece.competition && competitionPiece.competition !== 'None'" class="comp-division">
                    · {{ competitionPiece.competition }}
                  </span>
                  <span v-else class="comp-division pending"> · Division pending</span>
                </div>
                <div v-if="needsSurveyUpdate" class="comp-survey-warn">
                  Your self-assessment needs an update so the IABF team can assign your division.
                </div>
              </div>

              <div class="comp-actions">
                <router-link
                  to="/competition"
                  class="iabf-btn iabf-btn--primary comp-cta-link"
                >
                  {{ needsSurveyUpdate ? 'Complete self-assessment →' : 'View competition details →' }}
                </router-link>
                <button
                  v-if="canChangeCompetitionPiece && approvedRows.length > 1"
                  type="button"
                  class="iabf-btn iabf-btn--secondary"
                  @click="openChangeCompModal"
                >
                  Change competition piece
                </button>
              </div>
              <p v-if="!canChangeCompetitionPiece" class="comp-hint">
                Competition piece changes are closed (final divisions are being set).
                Email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> if you need help.
              </p>
              <p v-if="compActionError" class="form-error" role="alert">{{ compActionError }}</p>
            </template>

            <template v-else-if="isCompetitionDeclined">
              <div class="comp-declined">
                <p class="comp-declined-status">
                  <strong>Not competing this year</strong>
                  <span class="comp-declined-sub"> — you’ll still perform in the showcase.</span>
                </p>
                <button
                  type="button"
                  class="piece-link-btn"
                  :disabled="competitionDeclineSaving"
                  @click="clearCompetitionDeclined"
                >
                  Changed my mind, enter competition
                </button>
                <p v-if="compActionError" class="form-error" role="alert">{{ compActionError }}</p>
              </div>
            </template>

            <template v-else>
              <p class="comp-change-label">Which piece would you like to enter? (optional)</p>
              <div class="comp-radio-list">
                <label
                  class="comp-radio"
                  :class="{ selected: selectedCompetitionPieceId === '' }"
                >
                  <input v-model="selectedCompetitionPieceId" type="radio" value="">
                  <span><strong>Don’t compete this year</strong>
                    <span class="comp-radio-sub"> — still perform in the showcase</span>
                  </span>
                </label>
                <label
                  v-for="p in approvedRows"
                  :key="'comp-new-' + p.id"
                  class="comp-radio"
                  :class="{ selected: selectedCompetitionPieceId === p.id }"
                >
                  <input v-model="selectedCompetitionPieceId" type="radio" :value="p.id">
                  <span>
                    <strong>{{ p.title || '(untitled)' }}</strong>
                    <span v-if="p.songArtist" class="comp-radio-sub"> — {{ p.songArtist }}</span>
                  </span>
                </label>
              </div>
              <div class="comp-actions">
                <router-link
                  v-if="selectedCompetitionPieceId"
                  :to="{ path: '/competition', query: { piece: selectedCompetitionPieceId } }"
                  class="iabf-btn iabf-btn--primary comp-cta-link"
                >
                  Continue to self-assessment →
                </router-link>
                <button
                  v-else
                  type="button"
                  class="iabf-btn iabf-btn--primary"
                  :disabled="competitionDeclineSaving"
                  @click="saveCompetitionDeclined"
                >
                  {{ competitionDeclineSaving ? 'Saving…' : 'Save — not competing' }}
                </button>
              </div>
              <p v-if="compActionError" class="form-error" role="alert">{{ compActionError }}</p>
            </template>
          </div>
        </div>
      </section>

      <!-- ═══ ADD A PIECE ═══ -->
      <section
        v-if="!isNotPerformingSolos && (!registrationClosed || addWizardOpen)"
        class="hub-section"
      >
        <div v-if="registrationClosed" class="iabf-banner deadline-notice">
          <strong>Showcase piece registration is now closed.</strong>
          You can still upload or update music for pieces you've already registered.
          To add or change a piece, email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
        </div>

        <template v-else>
          <div class="hub-panel iabf-card" :class="{ 'is-expanded': addWizardOpen }">
            <button type="button" class="hub-panel-toggle" @click="toggleAddWizard">
              <div>
                <span class="hub-panel-kicker">Open</span>
                <h2 class="hub-panel-title">Add a piece</h2>
                <p class="hub-panel-summary">
                  {{ canAddSolo
                    ? 'Check the lineup, then register title, type, and program credits.'
                    : 'You have 3 solos. You can still add a small group, or email hello@iabf.dance to replace a solo.' }}
                </p>
              </div>
              <span class="slot-chev">{{ addWizardOpen ? '▴' : '▾' }}</span>
            </button>

            <div v-if="addWizardOpen" class="hub-panel-body add-wizard">


              <div class="wizard-steps" aria-label="Registration steps">
                <span
                  v-for="s in 3"
                  :key="'ws-' + s"
                  class="wizard-step-dot"
                  :class="{ active: addStep === s, done: addStep > s }"
                >{{ s }}</span>
              </div>

              <!-- Step 1: Availability (mirrors How it works) -->
              <div v-if="addStep === 1" class="wizard-pane">
                <h3 class="wizard-pane-title">1. Check availability</h3>

                <h4 class="how-h">Classical variations</h4>
                <p class="wizard-pane-lede">
                  Classical variations are first-come, first served.
                  <button type="button" class="inline-link-btn" @click="openLineupSearch">
                    Search the showcase lineup
                  </button>
                  to check if a variation is still available.
                </p>

                <h4 class="how-h">Pop music</h4>
                <p class="wizard-pane-lede">
                  Pop or licensed music is subject to availability.
                  Please <a href="https://www.ascap.com/repertory" target="_blank" rel="noopener">search ASCAP</a>
                  before registering.
                </p>
                <p class="how-video-link">
                  <a
                    href="https://cdn.jwplayer.com/players/PAvHMMzj-ZbyADQFv.html"
                    target="_blank"
                    rel="noopener"
                  >▶ Watch: how to look up music in the ASCAP database</a>
                  <span class="how-video-meta"> (~3 min)</span>
                </p>

                <h4 class="how-h">Music files</h4>
                <p class="wizard-pane-lede">
                  Please provide your music files after registration, trimmed to under <strong>3:15</strong>.
                  <strong>WAV, MP3, or M4A</strong> accepted.
                  Small groups share that music toward each dancer’s 5½ minutes.
                </p>

                <div class="wizard-actions">
                  <button type="button" class="iabf-btn iabf-btn--primary" @click="addStep = 2">
                    Looks free — continue →
                  </button>
                </div>
              </div>

              <!-- Step 2: Piece details -->
              <div v-else-if="addStep === 2" class="wizard-pane">
                <h3 class="wizard-pane-title">2. Piece details</h3>
                <v-form ref="detailsForm" v-model="detailsValid">
                  <p class="form-question">Variation or song title *</p>
                  <v-text-field
                    v-model="regForm.title"
                    :rules="[v => !!v || 'Title is required']"
                    required
                  />
                  <p class="form-question">Song artist / composer *</p>
                  <v-text-field
                    v-model="regForm.songArtist"
                    :rules="[v => !!v || 'Artist/Composer is required']"
                    required
                  />
                  <p class="form-question">Piece type *</p>
                  <div class="piece-type-hint">
                    <p>
                      A classical variation is something like Aurora from
                      <em>Sleeping Beauty</em>, Kitri from <em>Don Quixote</em>,
                      or Myrtha from <em>Giselle</em>.
                    </p>
                    <p>
                      If you’re not doing one of the classics, choose original ballet
                      if your dance is mostly classical ballet steps, contemporary if
                      it’s contemporary or lyrical, or jazz if it’s jazz.
                    </p>
                  </div>
                  <div class="feeling-chips" role="group" aria-label="Piece type">
                    <button
                      v-for="opt in pieceTypeOptions"
                      :key="opt"
                      type="button"
                      class="feeling-chip"
                      :class="{ selected: regForm.pieceType === opt }"
                      @click="regForm.pieceType = opt; detailsError = ''"
                    >
                      {{ opt }}
                    </button>
                  </div>
                  <p class="form-question">Group size *</p>
                  <div class="feeling-chips" role="group" aria-label="Group size">
                    <button
                      v-for="opt in groupSizeOptions"
                      :key="opt"
                      type="button"
                      class="feeling-chip"
                      :class="{ selected: regForm.groupSize === opt }"
                      :disabled="opt === 'Solo' && !canAddSolo"
                      @click="onPickGroupSize(opt)"
                    >
                      {{ opt }}
                    </button>
                  </div>
                  <p v-if="!canAddSolo" class="group-split-hint">
                    You already have 3 solos. A small group is still welcome.
                  </p>
                  <p
                    v-if="regForm.groupSize && regForm.groupSize !== 'Solo'"
                    class="group-split-hint"
                  >
                    This piece’s music length will be split evenly among the dancers.
                  </p>
                  <div v-if="regForm.groupSize && regForm.groupSize !== 'Solo'">
                    <p class="form-question">Names of other dancers in this piece</p>
                    <v-text-field
                      v-model="regForm.groupMemberNames"
                      hint="List other dancer names, separated by commas"
                      persistent-hint
                    />
                  </div>
                  <p v-if="detailsError" class="form-error" role="alert">{{ detailsError }}</p>
                </v-form>
                <div class="wizard-actions">
                  <button type="button" class="wizard-back" @click="addStep = 1">← Back</button>
                  <button type="button" class="iabf-btn iabf-btn--primary" @click="goToProgramStep">
                    Continue →
                  </button>
                </div>
              </div>

              <!-- Step 3: Program + submit -->
              <div v-else class="wizard-pane">
                <h3 class="wizard-pane-title">3. Program credits</h3>
                <v-form ref="registrationForm" v-model="registrationValid" @submit.prevent="submitRegistration">
                  <p class="form-question">How would you like your name displayed in the program? *</p>
                  <v-text-field
                    v-model="regForm.programDancer"
                    :rules="[v => !!v || 'Program name is required']"
                    required
                  />
                  <p class="form-question">Teacher / choreographer credit</p>
                  <v-text-field v-model="regForm.programTeacher" />
                  <p class="form-question">Notes (optional)</p>
                  <v-text-field
                    v-model="regForm.notes"
                    hint='e.g. "need help finding a teacher", "replacing my previous piece", "License arranged with musician"'
                    persistent-hint
                  />
                  <p class="wizard-music-note">
                    You’ll upload trimmed music (≤ 3:15) from your piece card after submission.
                  </p>
                  <p class="wizard-review-note">
                    This is a request for final approval. The IABF team will review your piece submission and confirm eligibility.
                  </p>
                  <p v-if="registrationError" class="form-error" role="alert">{{ registrationError }}</p>
                  <div class="wizard-actions">
                    <button type="button" class="wizard-back" @click="addStep = 2">← Back</button>
                    <v-btn
                      color="primary"
                      dark
                      type="submit"
                      :disabled="!registrationValid || registrationLoading"
                      :loading="registrationLoading"
                    >
                      {{ registrationLoading ? 'Submitting…' : 'Submit piece request' }}
                    </v-btn>
                  </div>
                </v-form>
              </div>
            </div>
          </div>
        </template>
      </section>

      <!-- ═══ FULL LINEUP (collapsed reference) ═══ -->
      <section v-if="!isNotPerformingSolos" class="hub-section hub-section-last">
        <div class="hub-panel iabf-card" :class="{ 'is-expanded': lineupPanelOpen }">
          <button type="button" class="hub-panel-toggle" @click="toggleLineupPanel">
            <div>
              <span class="hub-panel-kicker">Reference</span>
              <h2 class="hub-panel-title">Full showcase lineup</h2>
              <p class="hub-panel-summary">Search every registered piece by title, artist, or type.</p>
            </div>
            <span class="slot-chev">{{ lineupPanelOpen ? '▴' : '▾' }}</span>
          </button>
          <div v-show="lineupPanelOpen" class="hub-panel-body">
            <p class="lineup-note">Use this to check if your variation or song is already in the show.</p>
            <div v-if="lineupLoading" class="lineup-loading">Loading lineup…</div>
            <vue-good-table
              v-else-if="lineupLoaded"
              :columns="lineupColumns"
              :rows="approvedLineupRows"
              :search-options="{
                enabled: true,
                placeholder: 'Search pieces…',
              }"
            >
              <div slot="emptystate">No pieces match your search.</div>
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'namedDancers'">
                  {{ listLineupDancerNames(props.row.dancers) }}
                </span>
                <span v-else>{{ props.formattedRow[props.column.field] }}</span>
              </template>
            </vue-good-table>
          </div>
        </div>
      </section>
    </div>

    <!-- Lineup search modal (from How it works / wizard links) -->
    <div v-if="lineupModalOpen" class="lineup-modal-root" @keydown.esc.prevent="closeLineupSearch">
      <div class="lineup-modal-backdrop" @click="closeLineupSearch" />
      <div
        class="lineup-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lineup-modal-title"
        @click.stop
      >
        <header class="lineup-modal-header">
          <h2 id="lineup-modal-title">Showcase lineup</h2>
          <button type="button" class="lineup-modal-close" aria-label="Close" @click="closeLineupSearch">×</button>
        </header>
        <p class="lineup-modal-lede">
          Search by title, artist, or type to see if your variation or song is already registered.
        </p>
        <div class="lineup-modal-body">
          <div v-if="lineupLoading" class="lineup-loading">Loading lineup…</div>
          <template v-else-if="lineupLoaded">
            <v-text-field
              v-model="lineupSearch"
              label="Search by title, artist, or type"
              clearable
              dense
              hide-details
              class="lineup-search"
              autofocus
            />
            <div v-if="!filteredLineupRows.length" class="lineup-empty">
              No pieces match that search — looking good!
            </div>
            <ul v-else class="lineup-mini-list lineup-modal-list">
              <li v-for="row in filteredLineupRows" :key="'modal-' + row.id">
                <strong>{{ row.title || '(untitled)' }}</strong>
                <span v-if="row.songArtist"> — {{ row.songArtist }}</span>
                <span class="lineup-mini-meta">
                  <template v-if="row.pieceType"> · {{ row.pieceType }}</template>
                  <template v-if="row.groupSize"> · {{ row.groupSize }}</template>
                </span>
              </li>
            </ul>
          </template>
          <p v-else class="lineup-prompt">Couldn’t load the lineup. Close and try again.</p>
        </div>
        <footer class="lineup-modal-footer">
          <button type="button" class="iabf-btn iabf-btn--primary" @click="closeLineupSearch">
            Done
          </button>
        </footer>
      </div>
    </div>

    <!-- Change competition piece modal -->
    <div
      v-if="changeCompModalOpen"
      class="lineup-modal-root"
      @keydown.esc.prevent="closeChangeCompModal"
    >
      <div class="lineup-modal-backdrop" @click="closeChangeCompModal" />
      <div
        class="lineup-modal program-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-comp-title"
        @click.stop
      >
        <header class="lineup-modal-header">
          <h2 id="change-comp-title">Change competition piece?</h2>
          <button
            type="button"
            class="lineup-modal-close"
            aria-label="Close"
            :disabled="changingCompetitionPiece"
            @click="closeChangeCompModal"
          >×</button>
        </header>
        <p class="lineup-modal-lede">
          You can change your competition piece until final divisions are set closer to IABF.
          <strong>This will clear your self-assessment answers</strong> — you’ll need to fill out
          a new form for the new piece.
        </p>
        <div class="lineup-modal-body">
          <p class="comp-change-label">Which piece should compete?</p>
          <div class="comp-radio-list">
            <label
              v-for="p in approvedRows"
              :key="'comp-modal-' + p.id"
              class="comp-radio"
              :class="{ selected: changeCompSelectedId === p.id }"
            >
              <input v-model="changeCompSelectedId" type="radio" :value="p.id">
              <span>
                <strong>{{ p.title || '(untitled)' }}</strong>
                <span v-if="p.songArtist" class="comp-radio-sub"> — {{ p.songArtist }}</span>
                <span v-if="p.id === competitionPieceId" class="comp-radio-sub"> (current)</span>
              </span>
            </label>
          </div>
          <p v-if="compActionError" class="form-error" role="alert">{{ compActionError }}</p>
        </div>
        <footer class="lineup-modal-footer program-edit-footer">
          <button
            type="button"
            class="iabf-btn iabf-btn--secondary"
            :disabled="changingCompetitionPiece"
            @click="closeChangeCompModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="iabf-btn iabf-btn--primary"
            :disabled="
              changingCompetitionPiece ||
              !changeCompSelectedId ||
              changeCompSelectedId === competitionPieceId
            "
            @click="confirmChangeCompetitionPiece"
          >
            {{ changingCompetitionPiece ? 'Updating…' : 'Clear survey & change piece' }}
          </button>
        </footer>
      </div>
    </div>

    <!-- Edit program modal -->
    <div
      v-if="dialog"
      class="lineup-modal-root"
      @keydown.esc.prevent="closeProgramEdit"
    >
      <div class="lineup-modal-backdrop" @click="closeProgramEdit" />
      <div
        class="lineup-modal program-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="program-edit-title"
        @click.stop
      >
        <header class="lineup-modal-header">
          <h2 id="program-edit-title">Edit program details</h2>
          <button
            type="button"
            class="lineup-modal-close"
            aria-label="Close"
            :disabled="programEditSaving"
            @click="closeProgramEdit"
          >×</button>
        </header>
        <p class="lineup-modal-lede">
          These names appear in the printed program. To change the music or variation, submit a new
          piece and note that it replaces this one. To add or remove cast members, email
          <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
        </p>
        <div class="lineup-modal-body program-edit-body">
          <label class="form-question" for="program-edit-name">
            How should your name appear in the program?
          </label>
          <input
            id="program-edit-name"
            v-model="editedProgramName"
            type="text"
            class="program-edit-input"
            autocomplete="name"
          >

          <label class="form-question" for="program-edit-coach">
            Choreographed/coached by
          </label>
          <p class="program-edit-hint">Leave blank if self-choreographed or self-rehearsed.</p>
          <input
            id="program-edit-coach"
            v-model="editedProgramChoreographer"
            type="text"
            class="program-edit-input"
            placeholder="Name of teacher or choreographer"
            autocomplete="off"
          >

          <label class="form-question" for="program-edit-title-field">
            Piece title in the program
          </label>
          <input
            id="program-edit-title-field"
            v-model="editedProgramTitle"
            type="text"
            class="program-edit-input"
            autocomplete="off"
          >

          <p v-if="programEditError" class="form-error program-edit-error" role="alert">
            {{ programEditError }}
          </p>
        </div>
        <footer class="lineup-modal-footer program-edit-footer">
          <button
            type="button"
            class="iabf-btn iabf-btn--secondary"
            :disabled="programEditSaving"
            @click="closeProgramEdit"
          >
            Cancel
          </button>
          <button
            type="button"
            class="iabf-btn iabf-btn--primary"
            :disabled="programEditSaving"
            @click="updatePiece"
          >
            {{ programEditSaving ? 'Saving…' : 'Save program' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import CompetitionAbout from '@/components/iabf/CompetitionAbout.vue'
import { IABF } from '@/config/iabf-collections'
import { IABF_DANCER_YEAR, storagePrefix } from '@/config/iabf-event'
import {
  isCompetitionPieceChangeClosed,
  isShowcaseRegistrationClosed
} from '@/config/iabf-registration-2027'
import { saveErrorMessage } from '@/utils/save-error'
import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table'
import { db, storage } from '@/firebase'
import {
  assertStageTimeOk,
  formatMusicTime as formatStageClock,
  isSoloPiece,
  MAX_SOLO_PIECES,
  pieceTimeLabelForDancer,
  soloPieceCount
} from '@/utils/iabf-stage-time'

export default {
  components: {
    CompetitionAbout,
    VueGoodTable
  },
  data () {
    return {
      programEditError: '',
      programEditSaving: false,
      registrationError: '',
      dialog: false,
      dancerLookup: {},
      rows: [],
      firstName: '',
      lastName: '',
      editedPieceId: '',
      editedProgramName: '',
      editedProgramChoreographer: '',
      editedProgramTitle: '',
      lineupRows: [],
      lineupDancerLookup: {},
      lineupLoaded: false,
      lineupLoading: false,
      lineupSearch: '',
      lineupPanelOpen: false,
      lineupModalOpen: false,
      lineupColumns: [
        { label: 'Song/Variation Title', field: 'title' },
        { label: 'Artist/Composer', field: 'songArtist' },
        { label: 'Piece Type', field: 'pieceType' },
        { label: 'Group Size', field: 'groupSize' }
      ],
      registrationValid: false,
      detailsValid: false,
      detailsError: '',
      registrationSuccess: false,
      registrationLoading: false,
      pieceTypeOptions: [
        'Classical Variation',
        'Original Ballet Choreography',
        'Contemporary/Lyrical Ballet',
        'Jazz'
      ],
      groupSizeOptions: [
        'Solo',
        'Duet/Pas de Deux',
        'Trio',
        '4-12 Dancers'
      ],
      regForm: {
        title: '',
        songArtist: '',
        pieceType: '',
        groupSize: '',
        groupMemberNames: '',
        programDancer: '',
        programTeacher: '',
        notes: ''
      },
      uploadingPieceId: null,
      pendingPiece: null,
      /** HTMLAudioElement currently playing a piece preview (pause others) */
      activeMusicPreviewEl: null,
      showcasePrepSaving: false,
      showcasePrepError: '',
      uploadError: {},
      meaningDraft: {},
      meaningDirty: {},
      meaningSavingId: null,
      meaningError: {},
      meaningSuccess: {},
      cancelingPieceId: null,
      cancelError: {},
      // Hub UI state
      openPieceId: null,
      howItWorksOpen: false,
      competitionOpen: false,
      addWizardOpen: false,
      addStep: 1,
      selectedCompetitionPieceId: '',
      changingCompetitionPiece: false,
      competitionDeclineSaving: false,
      changeCompModalOpen: false,
      changeCompSelectedId: '',
      compActionError: ''
    }
  },
  computed: {
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    dancerId () {
      return this.currentIABFDancer ? this.currentIABFDancer.id : null
    },
    competitionPieceId () {
      return this.currentIABFDancer ? this.currentIABFDancer.competitionPieceId : null
    },
    isCompetitionRegistered () {
      return !!(this.currentIABFDancer && this.currentIABFDancer.competitionRegistered)
    },
    competitionPiece () {
      if (!this.competitionPieceId) return null
      return this.rows.find(p => p.id === this.competitionPieceId) || null
    },
    needsSurveyUpdate () {
      if (!this.isCompetitionRegistered) return false
      const s = this.currentIABFDancer && this.currentIABFDancer.competitionSurvey
      if (!s) return true
      return Object.values(s).every(v => v === '' || v == null)
    },
    competitionSummary () {
      if (!this.rows.length) {
        return 'Register a piece first, then optionally enter one for judging.'
      }
      if (this.isCompetitionRegistered && this.competitionPiece) {
        return `Entered: ${this.competitionPiece.title || 'your piece'}${this.needsSurveyUpdate ? ' · survey needed' : ''}`
      }
      if (this.isCompetitionRegistered) {
        return 'You’re registered — division pending.'
      }
      if (this.isCompetitionDeclined) {
        return 'Not competing this year'
      }
      return 'Optional — enter one piece for feedback and medals.'
    },
    isCompetitionDeclined () {
      return !!(this.currentIABFDancer && this.currentIABFDancer.competitionDeclined) &&
        !this.isCompetitionRegistered
    },
    /** Entered or explicitly declined — needed before overall “All set”. */
    isCompetitionDecided () {
      return this.isCompetitionRegistered || this.isCompetitionDeclined
    },
    canChangeCompetitionPiece () {
      return !isCompetitionPieceChangeClosed(this.festivalYear)
    },
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) ||
        Number(this.currentIABFDancer && this.currentIABFDancer.festivalYear) ||
        IABF_DANCER_YEAR
    },
    registrationClosed () {
      return isShowcaseRegistrationClosed(this.festivalYear)
    },
    soloCount () {
      return soloPieceCount(this.rows)
    },
    canAddSolo () {
      return this.soloCount < MAX_SOLO_PIECES
    },
    showcasePrepStatus () {
      return (this.currentIABFDancer && this.currentIABFDancer.showcasePrepStatus) || null
    },
    /** Opted out of solos — hide piece list, competition, add, lineup. */
    isNotPerformingSolos () {
      return this.showcasePrepStatus === 'not_performing' && !(this.rows && this.rows.length)
    },
    pendingPieceCount () {
      return (this.rows || []).filter(p => this.isPiecePending(p)).length
    },
    /** True when an approved piece is missing a usable music file. */
    hasMusicStillNeeded () {
      return (this.rows || []).some(p => this.pieceMissingMusic(p))
    },
    /**
     * Can mark all set only when there is at least one approved piece and
     * every approved piece has music. Pending-only or missing music → no.
     */
    canMarkShowcaseAllSet () {
      const approved = this.approvedRows || []
      if (!approved.length) {
        return false
      }
      return approved.every(p => this.pieceHasMusic(p))
    },
    /** Pieces marked done (music ready) — not overall festival all-set. */
    isShowcasePiecesDone () {
      return this.showcasePrepStatus === 'all_set' && this.canMarkShowcaseAllSet
    },
    /** Prep card only for empty / not-performing — not for “I’m done”. */
    showShowcasePrepCard () {
      if (this.isNotPerformingSolos) {
        return true
      }
      if (!this.rows.length) {
        return true
      }
      return false
    },
    /** Approved pieces only (for competition entry, etc.). */
    approvedRows () {
      return (this.rows || []).filter(p => !this.isPiecePending(p))
    },

    /** Public / availability search — approved pieces only. */
    approvedLineupRows () {
      return (this.lineupRows || []).filter(r =>
        !r.canceledByDancer && (r.approvalStatus || 'approved') === 'approved'
      )
    },
    filteredLineupRows () {
      const q = (this.lineupSearch || '').trim().toLowerCase()
      const base = this.approvedLineupRows
      if (!q) return base
      return base.filter(r => {
        const hay = [r.title, r.songArtist, r.pieceType, r.groupSize]
          .map(x => String(x || '').toLowerCase())
          .join(' ')
        return hay.includes(q)
      })
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    if (this.dancerId) {
      this.loadPieces()
    }
  },
  beforeDestroy () {
    document.body.style.overflow = ''
    this.stopMusicPreviews()
  },
  watch: {
    currentIABFDancer (val) {
      if (val && val.id && !this.firstName) {
        this.loadPieces()
      }
    },
    rows (list) {
      if (list && list.length && !this.openPieceId) {
        this.openPieceId = list[0].id
      }
    },
    competitionPieceId: {
      immediate: true,
      handler (id) {
        if (id) {
          this.selectedCompetitionPieceId = id
        }
      }
    }
  },
  methods: {
    async loadPieces () {
      if (this.$store.state.masqueradeDancerId && !this._masqueradeRefreshed) {
        this._masqueradeRefreshed = true
        try {
          await this.$store.dispatch('refreshEffectiveDancer')
        } catch (e) {
          console.warn('Masquerade dancer refresh failed', e)
        }
      }
      this.firstName = this.currentIABFDancer.firstName
      this.lastName = this.currentIABFDancer.lastName
      this.$store.dispatch('loadIABFPiecesForDancer', this.dancerId).then(() => {
        this.rows = this.$store.getters.loadedIABFPiecesforDancer || []
        if (this.rows.length) {
          this.openPieceId = this.rows[0].id
          // Returners: collapse guide; open competition if not entered yet
          this.howItWorksOpen = false
          this.competitionOpen = !this.isCompetitionRegistered || this.needsSurveyUpdate
          this.addWizardOpen = false
        } else {
          // First-timers: open how-it-works lightly + add wizard
          this.howItWorksOpen = false
          this.competitionOpen = false
          this.addWizardOpen = !this.registrationClosed
          this.addStep = 1
        }
        if (this.competitionPieceId) {
          this.selectedCompetitionPieceId = this.competitionPieceId
        }
        let loadedDancers = false
        this.rows.forEach(piece => {
          if (piece.pieceType !== 'solo' && !loadedDancers) {
            this.$store.dispatch('loadIABFDancers').then(() => {
              loadedDancers = true
              this.$store.getters.loadedIABFDancers.forEach(dancer => {
                this.dancerLookup[dancer.id] = dancer.firstName + ' ' + dancer.lastName
              })
            })
          }
        })
      })
    },
    togglePieceOpen (id) {
      this.openPieceId = this.openPieceId === id ? null : id
    },
    startAddPiece () {
      if (this.registrationClosed) return
      if (!this.canAddSolo && this.regForm.groupSize === 'Solo') {
        this.regForm.groupSize = ''
      }
      this.addWizardOpen = true
      this.addStep = 1
      this.registrationSuccess = false
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.add-wizard')
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    toggleAddWizard () {
      this.addWizardOpen = !this.addWizardOpen
      if (this.addWizardOpen && !this.canAddSolo && this.regForm.groupSize === 'Solo') {
        this.regForm.groupSize = ''
      }
    },
    onPickGroupSize (opt) {
      if (opt === 'Solo' && !this.canAddSolo) {
        this.detailsError = 'You already have 3 solos. A small group is still welcome — or email hello@iabf.dance if you need to replace a solo.'
        return
      }
      this.regForm.groupSize = opt
      this.detailsError = ''
    },
    toggleLineupPanel () {
      this.lineupPanelOpen = !this.lineupPanelOpen
      if (this.lineupPanelOpen) {
        this.ensureLineupLoaded()
      }
    },
    openLineupSearch () {
      this.lineupModalOpen = true
      this.ensureLineupLoaded()
      this.$nextTick(() => {
        document.body.style.overflow = 'hidden'
      })
    },
    closeLineupSearch () {
      this.lineupModalOpen = false
      document.body.style.overflow = ''
    },
    ensureLineupLoaded () {
      if (this.lineupLoaded || this.lineupLoading) return
      this.lineupLoading = true
      this.$store.dispatch('loadIABFPieces').then(() => {
        this.lineupRows = this.$store.getters.loadedIABFPieces || []
        this.lineupLoaded = true
        this.lineupLoading = false
      }).catch(() => {
        this.lineupLoading = false
      })
    },
    goToProgramStep () {
      this.detailsError = ''
      if (this.$refs.detailsForm) {
        this.$refs.detailsForm.validate()
      }
      if (!String(this.regForm.title || '').trim()) {
        this.detailsError = 'Add a variation or song title.'
        return
      }
      if (!String(this.regForm.songArtist || '').trim()) {
        this.detailsError = 'Add a song artist or composer.'
        return
      }
      if (!this.regForm.pieceType) {
        this.detailsError = 'Choose a piece type.'
        return
      }
      if (!this.regForm.groupSize) {
        this.detailsError = 'Choose a group size.'
        return
      }
      if (isSoloPiece(this.regForm) && !this.canAddSolo) {
        this.detailsError = 'You already have 3 solos. A small group is still welcome — or email hello@iabf.dance if you need to replace a solo.'
        return
      }
      // Prefill program name from dancer if empty
      if (!this.regForm.programDancer && this.firstName) {
        this.regForm.programDancer = `${this.firstName} ${this.lastName || ''}`.trim()
      }
      this.addStep = 3
    },
    openChangeCompModal () {
      if (!this.canChangeCompetitionPiece) {
        this.compActionError = 'Competition piece changes are closed. Email hello@iabf.dance if you need help.'
        return
      }
      this.compActionError = ''
      this.changeCompSelectedId = this.competitionPieceId || ''
      this.changeCompModalOpen = true
      this.$nextTick(() => {
        document.body.style.overflow = 'hidden'
      })
    },
    closeChangeCompModal () {
      if (this.changingCompetitionPiece) {
        return
      }
      this.changeCompModalOpen = false
      this.compActionError = ''
      document.body.style.overflow = ''
    },
    async confirmChangeCompetitionPiece () {
      this.compActionError = ''
      if (!this.canChangeCompetitionPiece) {
        this.compActionError = 'Competition piece changes are closed. Email hello@iabf.dance if you need help.'
        return
      }
      if (!this.changeCompSelectedId || !this.dancerId) {
        return
      }
      if (this.changeCompSelectedId === this.competitionPieceId) {
        this.compActionError = 'Choose a different piece, or cancel.'
        return
      }
      this.changingCompetitionPiece = true
      try {
        await this.$store.dispatch('updateCompetitionPiece', {
          dancerId: this.dancerId,
          pieceId: this.changeCompSelectedId
        })
        await this.$store.dispatch('loadIABFDancer', this.dancerId)
        this.selectedCompetitionPieceId = this.changeCompSelectedId
        this.changeCompModalOpen = false
        document.body.style.overflow = ''
        // Survey was cleared — send them to re-do self-assessment
        this.$router.push({
          path: '/competition',
          query: { piece: this.changeCompSelectedId }
        })
      } catch (e) {
        this.compActionError = saveErrorMessage(e, 'Could not update your competition piece. Please try again.')
      }
      this.changingCompetitionPiece = false
    },
    async saveCompetitionDeclined () {
      if (!this.dancerId) {
        this.compActionError = 'Your registration is still loading. Try again in a moment.'
        return
      }
      this.competitionDeclineSaving = true
      this.compActionError = ''
      try {
        await this.$store.dispatch('setCompetitionDeclined', {
          dancerId: this.dancerId,
          declined: true
        })
        this.selectedCompetitionPieceId = ''
      } catch (e) {
        this.compActionError = saveErrorMessage(e, 'Could not save your competition choice.')
      }
      this.competitionDeclineSaving = false
    },
    async clearCompetitionDeclined () {
      if (!this.dancerId) {
        this.compActionError = 'Your registration is still loading. Try again in a moment.'
        return
      }
      this.competitionDeclineSaving = true
      this.compActionError = ''
      try {
        await this.$store.dispatch('setCompetitionDeclined', {
          dancerId: this.dancerId,
          declined: false
        })
        this.selectedCompetitionPieceId = ''
      } catch (e) {
        this.compActionError = saveErrorMessage(e, 'Could not update your competition choice.')
      }
      this.competitionDeclineSaving = false
    },
    /** Pause any other piece’s preview when one starts playing. */
    onMusicPreviewPlay (pieceId, event) {
      const el = event && event.target
      if (this.activeMusicPreviewEl && this.activeMusicPreviewEl !== el) {
        try {
          this.activeMusicPreviewEl.pause()
        } catch (e) { /* noop */ }
      }
      this.activeMusicPreviewEl = el || null
    },
    stopMusicPreviews () {
      if (this.activeMusicPreviewEl) {
        try {
          this.activeMusicPreviewEl.pause()
        } catch (e) { /* noop */ }
      }
      this.activeMusicPreviewEl = null
    },
    async pickFile (piece) {
      if (this.isPiecePending(piece)) {
        this.$set(this.uploadError, piece.id, 'Music upload is available after your piece is approved.')
        return
      }
      if (!(piece && Array.isArray(piece.dancers) && piece.dancers.includes(this.dancerId))) {
        this.$set(this.uploadError, piece.id, 'You can only upload music for your own pieces.')
        return
      }
      this.pendingPiece = piece
      this.$set(this.uploadError, piece.id, '')
      await this.$nextTick()
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
        this.$refs.fileInput.click()
      }
    },
    async onFileChosen (event) {
      const file = event.target.files && event.target.files[0]
      const piece = this.pendingPiece
      this.pendingPiece = null
      if (!file || !piece) return
      this.uploadingPieceId = piece.id
      this.$set(this.uploadError, piece.id, '')
      this.stopMusicPreviews()
      try {
        const ext = (file.name.split('.').pop() || '').toLowerCase()
        if (!['mp3', 'm4a', 'wav'].includes(ext)) {
          throw new Error('Please choose an .mp3, .m4a, or .wav file.')
        }
        const seconds = await this.readAudioDuration(file)
        if (seconds <= 0) {
          throw new Error('Could not read the audio length — please try a different file.')
        }
        const yearPieces = await this.yearPiecesForValidation()
        const check = assertStageTimeOk({
          allPieces: yearPieces,
          proposedPiece: { ...piece, lengthInSeconds: seconds },
          nameForId: (id) => this.dancerLookup[id] || (id === this.dancerId ? 'you' : '')
        })
        if (!check.ok) {
          throw new Error(check.error)
        }
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_')
        const year = this.festivalYear || IABF_DANCER_YEAR
        const path = `${storagePrefix('music', year)}/${piece.id}-${safeName}`
        const ref = storage.ref(path)
        const snap = await ref.put(file, { contentType: file.type || 'audio/mpeg' })
        const downloadUrl = await snap.ref.getDownloadURL()
        await db.collection(IABF.pieces).doc(piece.id).update({
          musicUrl: downloadUrl,
          musicFileName: file.name,
          musicReceived: true,
          lengthInSeconds: seconds
        })
        const idx = this.rows.findIndex(r => r.id === piece.id)
        if (idx !== -1) {
          this.$set(this.rows, idx, {
            ...this.rows[idx],
            musicUrl: downloadUrl,
            musicFileName: file.name,
            musicReceived: true,
            lengthInSeconds: seconds
          })
        }
        if (this._yearPieces) {
          const yIdx = this._yearPieces.findIndex(r => r.id === piece.id)
          const updated = {
            ...(yIdx === -1 ? piece : this._yearPieces[yIdx]),
            musicUrl: downloadUrl,
            musicFileName: file.name,
            musicReceived: true,
            lengthInSeconds: seconds
          }
          if (yIdx === -1) {
            this._yearPieces.push(updated)
          } else {
            this.$set(this._yearPieces, yIdx, updated)
          }
        }
      } catch (e) {
        console.error('Music upload failed:', e)
        this.$set(this.uploadError, piece.id, e.message || 'Upload failed — please try again.')
      }
      this.uploadingPieceId = null
    },
    readAudioDuration (file) {
      return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file)
        const audio = new Audio()
        const cleanup = () => { try { URL.revokeObjectURL(objectUrl) } catch (e) { /* noop */ } }
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('Reading audio metadata took too long. Try a different file.'))
        }, 12000)
        audio.addEventListener('loadedmetadata', () => {
          clearTimeout(timeout)
          cleanup()
          const sec = Number(audio.duration)
          if (!Number.isFinite(sec)) return reject(new Error('Could not read audio length.'))
          resolve(Math.round(sec))
        })
        audio.addEventListener('error', () => {
          clearTimeout(timeout)
          cleanup()
          reject(new Error('Could not read the audio file.'))
        })
        audio.src = objectUrl
      })
    },
    setEditedPieceId (piece) {
      this.editedPieceId = piece.id
      this.editedProgramName = piece.programDancer || this.listDancerNames(piece.dancers)
      this.editedProgramChoreographer = piece.programTeacher || ''
      this.editedProgramTitle = piece.programTitle ||
        ((piece.title || '') + (piece.songArtist ? ' — ' + piece.songArtist : ''))
      this.programEditError = ''
      this.dialog = true
      this.$nextTick(() => {
        document.body.style.overflow = 'hidden'
      })
    },
    closeProgramEdit () {
      if (this.programEditSaving) {
        return
      }
      this.dialog = false
      this.programEditError = ''
      document.body.style.overflow = ''
    },
    onMeaningInput (pieceId, event) {
      const val = event && event.target ? event.target.value : ''
      this.$set(this.meaningDraft, pieceId, val)
      this.$set(this.meaningDirty, pieceId, true)
      this.$set(this.meaningSuccess, pieceId, false)
      this.$set(this.meaningError, pieceId, '')
    },
    async saveMeaning (piece) {
      if (!piece || !this.dancerId) return
      if (this.isPiecePending(piece)) {
        this.$set(this.meaningError, piece.id, 'You can share your story after the piece is approved.')
        return
      }
      const text = this.meaningDraft[piece.id] != null
        ? this.meaningDraft[piece.id]
        : (piece.meaningToMe || '')
      this.meaningSavingId = piece.id
      this.$set(this.meaningError, piece.id, '')
      try {
        const saved = await this.$store.dispatch('updatePieceMeaning', {
          pieceId: piece.id,
          dancerId: this.dancerId,
          meaningToMe: text
        })
        const idx = this.rows.findIndex(r => r.id === piece.id)
        if (idx !== -1) {
          this.$set(this.rows, idx, { ...this.rows[idx], meaningToMe: saved })
        }
        this.$set(this.meaningDraft, piece.id, saved)
        this.$set(this.meaningDirty, piece.id, false)
        this.$set(this.meaningSuccess, piece.id, true)
      } catch (e) {
        this.$set(this.meaningError, piece.id, saveErrorMessage(e, 'Could not save your story.'))
      }
      this.meaningSavingId = null
    },
    async confirmCancelPiece (piece) {
      if (!piece || !this.dancerId) return
      const ok = window.confirm(
        `Remove “${piece.title || 'this piece'}” from your showcase?\n\n` +
        'The IABF team will be notified. If this was your competition entry, that will be cleared too.'
      )
      if (!ok) return
      this.cancelingPieceId = piece.id
      this.$set(this.cancelError, piece.id, '')
      try {
        await this.$store.dispatch('cancelOwnPiece', {
          pieceId: piece.id,
          dancerId: this.dancerId
        })
        this.rows = this.rows.filter(r => r.id !== piece.id)
        if (this.openPieceId === piece.id) {
          this.openPieceId = this.rows[0] ? this.rows[0].id : null
        }
        // No pieces left — clear "all set" so the dashboard doesn't stay done empty
        if (!this.rows.length && this.showcasePrepStatus === 'all_set' && this.dancerId) {
          await this.$store.dispatch('setShowcasePrepStatus', {
            dancerId: this.dancerId,
            status: null
          }).catch(() => {})
        }
        // Refresh dancer so competition flags update if needed
        if (this.dancerId) {
          await this.$store.dispatch('loadIABFDancer', this.dancerId).catch(() => {})
        }
      } catch (e) {
        this.$set(this.cancelError, piece.id, saveErrorMessage(e, 'Could not remove this piece.'))
      }
      this.cancelingPieceId = null
    },
    updatePiece () {
      this.programEditError = ''
      this.programEditSaving = true
      this.$store.dispatch('updatePieceDetails', {
        id: this.editedPieceId,
        programDancer: this.editedProgramName,
        programTeacher: this.editedProgramChoreographer,
        programTitle: this.editedProgramTitle
      }).then(() => {
        this.rows = this.rows.map(piece => {
          if (piece.id === this.editedPieceId) {
            piece.programDancer = this.editedProgramName
            piece.programTeacher = this.editedProgramChoreographer
            piece.programTitle = this.editedProgramTitle
          }
          return piece
        })
        this.dialog = false
        this.programEditSaving = false
        document.body.style.overflow = ''
      }).catch((err) => {
        this.programEditError = saveErrorMessage(err, 'Could not update program details. Please try again.')
        this.programEditSaving = false
      })
    },
    timeInMinutes (seconds) {
      return formatStageClock(seconds)
    },
    formatMusicTime (seconds) {
      return formatStageClock(seconds)
    },
    pieceTimeLabel (piece) {
      return pieceTimeLabelForDancer(piece, this.dancerId)
    },
    async yearPiecesForValidation () {
      if (this._yearPieces && this._yearPieces.length) {
        return this._yearPieces
      }
      const snap = await db.collection(IABF.pieces).get()
      this._yearPieces = snap.docs.map((doc) => ({ id: doc.id, ...(doc.data() || {}) }))
      return this._yearPieces
    },
    isPiecePending (piece) {
      return piece && piece.approvalStatus === 'pending'
    },
    pieceHasMusic (piece) {
      if (!piece) {
        return false
      }
      return !!(piece.musicReceived && piece.musicUrl)
    },
    pieceMissingMusic (piece) {
      if (!piece || this.isPiecePending(piece)) {
        return false
      }
      return !this.pieceHasMusic(piece)
    },
    async setShowcasePrep (status) {
      if (!this.dancerId) {
        this.showcasePrepError = 'Your registration is still loading. Try again in a moment.'
        return
      }
      if (status === 'all_set') {
        if (this.hasMusicStillNeeded || !this.canMarkShowcaseAllSet) {
          this.showcasePrepError = 'Upload music for every approved piece before marking all set.'
          return
        }
      }
      if (status === 'not_performing' && this.rows.length) {
        this.showcasePrepError = 'Remove your pieces first, or keep them and mark “I’m done” instead.'
        return
      }
      this.showcasePrepSaving = true
      this.showcasePrepError = ''
      try {
        await this.$store.dispatch('setShowcasePrepStatus', {
          dancerId: this.dancerId,
          status
        })
        if (status === 'all_set') {
          this.addWizardOpen = false
        }
      } catch (e) {
        this.showcasePrepError = saveErrorMessage(e, 'Could not update your showcase status.')
      }
      this.showcasePrepSaving = false
    },
    /** Open add flow; clear “I’m done” if they were marked finished. */
    async onAddAnotherPiece () {
      if (this.isShowcasePiecesDone) {
        await this.setShowcasePrep(null)
        if (this.showcasePrepError) {
          return
        }
      }
      this.startAddPiece()
    },
    submitRegistration () {
      this.registrationError = ''
      if (!this.$refs.registrationForm || !this.$refs.registrationForm.validate()) {
        this.registrationError = 'Please fill in all required fields above.'
        return
      }
      if (!this.dancerId) {
        this.registrationError = 'Your registration is still loading. Wait a moment and try again.'
        return
      }
      if (isSoloPiece(this.regForm) && !this.canAddSolo) {
        this.registrationError = 'You already have 3 solos. A small group is still welcome — or email hello@iabf.dance if you need to replace a solo.'
        return
      }
      this.registrationLoading = true
      this.$store.dispatch('submitPieceRegistration', {
        dancerId: this.dancerId,
        piece: { ...this.regForm }
      }).then((created) => {
        this.registrationLoading = false
        if (this.$refs.registrationForm) this.$refs.registrationForm.reset()
        if (this.$refs.detailsForm) this.$refs.detailsForm.reset()
        this.regForm = {
          title: '',
          songArtist: '',
          pieceType: '',
          groupSize: '',
          groupMemberNames: '',
          programDancer: '',
          programTeacher: '',
          notes: ''
        }
        this.addStep = 1
        this.addWizardOpen = false
        this.registrationSuccess = false
        // Reload pieces so the pending card appears at the top of Your pieces
        return this.$store.dispatch('loadIABFPiecesForDancer', this.dancerId).then(() => {
          this.rows = this.$store.getters.loadedIABFPiecesforDancer || []
          const newId = created && created.id
          const pending = newId
            ? this.rows.find(r => r.id === newId)
            : this.rows.find(r => r.approvalStatus === 'pending')
          this.openPieceId = pending ? pending.id : (this.rows[0] && this.rows[0].id) || null
          this.$nextTick(() => {
            const el = this.$el && this.$el.querySelector('.hub-section')
            // Prefer scrolling to Your pieces section
            const sections = this.$el && this.$el.querySelectorAll('.hub-section')
            const yourPieces = sections && sections[1] ? sections[1] : el
            if (yourPieces && yourPieces.scrollIntoView) {
              yourPieces.scrollIntoView({ behavior: 'smooth', block: 'start' })
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          })
        })
      }).catch((err) => {
        this.registrationError = saveErrorMessage(err, 'Could not submit piece registration. Please try again or email hello@iabf.dance.')
        this.registrationLoading = false
      })
    },
    listLineupDancerNames (dancers) {
      if (!dancers) return ''
      return dancers.map(id => this.lineupDancerLookup[id] || id).join(', ')
    },
    listDancerNames (dancers) {
      if (Object.keys(this.dancerLookup).length === 0) {
        return this.firstName + ' ' + this.lastName
      }
      return dancers.map(dancer => this.dancerLookup[dancer]).join(', ')
    }
  }
}
</script>

<style scoped>
.showcase-page {
  min-height: 100%;
  padding: 24px 16px 64px;
  background: var(--iabf-bg, #f6f4f1);
  color: var(--iabf-ink, #1c1b1f);
}
.showcase-inner {
  max-width: 760px;
  margin: 0 auto;
}

.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
  transition: color 0.15s;
}
.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}

.showcase-hero h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  line-height: 1.25;
  color: var(--iabf-ink, #1c1b1f);
  margin: 12px 0 8px;
}
.pieces-lede {
  font-size: 16px;
  line-height: 1.55;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 18px;
}
.inline-link-btn {
  display: inline;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font: inherit;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  cursor: pointer;
  text-decoration: underline;
}
.inline-link-btn:hover {
  color: var(--iabf-ink, #1c1b1f);
}

.lineup-modal-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 12px;
  overflow-y: auto;
}
.lineup-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 27, 31, 0.45);
}
.lineup-modal {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 520px;
  margin: 12px auto 40px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 48px rgba(28, 27, 31, 0.18);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 48px);
}
.lineup-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 0;
}
.lineup-modal-header h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  margin: 0;
  color: var(--iabf-ink, #1c1b1f);
}
.lineup-modal-close {
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: var(--iabf-ink-faint, #8a8792);
  cursor: pointer;
  padding: 0 4px;
  margin: 0;
}
.lineup-modal-lede {
  margin: 8px 20px 0;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.45;
}
.lineup-modal-body {
  padding: 12px 20px 8px;
  overflow-y: auto;
  flex: 1;
}
.lineup-modal-list {
  max-height: none;
  margin-top: 10px;
}
.lineup-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 18px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  flex-shrink: 0;
}
.lineup-modal-lede a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
  text-decoration: underline;
}
.program-edit-modal {
  max-width: 480px;
}
.program-edit-body .form-question {
  display: block;
  margin: 14px 0 6px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 16px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}
.program-edit-body .form-question:first-child {
  margin-top: 4px;
}
.program-edit-hint {
  margin: -2px 0 8px;
  font-size: 13px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.4;
}
.program-edit-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  padding: 12px 14px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  background: #fff;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.program-edit-input:focus {
  border-color: var(--iabf-primary, #8a957d);
  box-shadow: 0 0 0 3px var(--iabf-focus-ring, rgba(138, 149, 125, 0.28));
}
.program-edit-input::placeholder {
  color: var(--iabf-ink-faint, #8a8792);
}
.program-edit-error {
  margin: 14px 0 0;
}
.program-edit-footer {
  flex-wrap: wrap;
}

.hub-section {
  margin-bottom: 18px;
}
.hub-section-last {
  margin-bottom: 0;
}
.showcase-prep-card {
  margin: 0 0 16px;
  padding: 16px 18px;
}
.showcase-prep-status {
  margin: 0 0 6px;
  font-size: 15px;
  line-height: 1.45;
  color: var(--iabf-ink, #1c1b1f);
}
.showcase-prep-hint {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
}
.showcase-prep-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.section-head {
  margin-bottom: 12px;
}
.section-head h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 24px;
  margin: 0 0 4px;
  color: var(--iabf-ink, #1c1b1f);
}
.section-lede {
  margin: 0;
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
}

.piece-slots {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.piece-slot {
  padding: 0;
  overflow: hidden;
}
.piece-slot.is-empty {
  border-style: dashed;
  background: rgba(255, 255, 255, 0.7);
}

.slot-empty {
  padding: 20px 20px 18px;
}
.add-another-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-top: 12px;
}
.add-another-row .form-error {
  flex-basis: 100%;
  margin: 0;
}
.slot-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--iabf-primary, #8a957d);
  background: var(--iabf-bg-soft, #eef0eb);
  padding: 3px 9px;
  border-radius: 999px;
  margin-bottom: 8px;
}
.slot-empty-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 20px;
  margin: 0 0 4px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.slot-empty-sub {
  margin: 0 0 14px;
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  line-height: 1.45;
}
.slot-add-btn {
  margin-top: 0;
}

.slot-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.slot-header:hover {
  background: rgba(238, 240, 235, 0.45);
}
.slot-header-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
}
.slot-header-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
}
.slot-chev {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  margin-left: 4px;
}
.slot-body {
  padding: 0 18px 18px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  padding-top: 14px;
}

.piece-card-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 20px;
  margin: 0 0 2px;
  color: var(--iabf-ink, #1c1b1f);
}
.piece-card-artist {
  margin: 0;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.piece-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  margin: 0 0 10px;
  font-size: 12px;
  color: var(--iabf-ink-faint, #8a8792);
}
.piece-card-meta span:not(.piece-length) {
  background: var(--iabf-bg-soft, #eef0eb);
  padding: 3px 10px;
  border-radius: 999px;
}
.piece-card-meta .piece-length {
  font-size: 13px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  background: none;
  padding: 0;
  border-radius: 0;
}
.piece-card-program {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
}
.piece-program-edit {
  margin-left: 8px;
  font-size: 13px;
  vertical-align: baseline;
}
.piece-card-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.piece-card-edit {
  border: 1px solid var(--iabf-border, #e3e0e8);
  background: #fff;
  border-radius: 10px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--iabf-primary, #8a957d);
  cursor: pointer;
  margin-top: 0;
}
.piece-card-edit:hover {
  background: var(--iabf-bg-soft, #eef0eb);
}
.piece-music-status {
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.piece-music-status.is-ok {
  color: var(--iabf-success, #5f7a55);
  font-weight: 600;
}
.piece-music-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.music-file-name {
  margin: 4px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: var(--iabf-ink-muted, #5c5a63);
  word-break: break-word;
}
.piece-music-player {
  display: block;
  width: 100%;
  max-width: 360px;
  margin-top: 10px;
  height: 36px;
}
.piece-link-btn {
  margin-top: 0;
  border: none;
  background: none;
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 14px;
}
.piece-upload-btn {
  margin-top: 0;
}
.meaning-block {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.meaning-label {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}
.meaning-hint {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
}
.meaning-input {
  width: 100%;
  box-sizing: border-box;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  padding: 10px 12px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink, #1c1b1f);
  resize: vertical;
  min-height: 72px;
}
.meaning-input:focus {
  outline: none;
  border-color: var(--iabf-primary, #8a957d);
}
.meaning-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}
.meaning-ok {
  font-size: 13px;
  font-weight: 600;
  color: var(--iabf-success, #5f7a55);
}
.piece-danger {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed var(--iabf-border, #e3e0e8);
}
.piece-delete-btn {
  border: none;
  background: none;
  color: var(--iabf-danger, #b54a4a);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: 0;
  text-decoration: underline;
}
.piece-delete-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.meta-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--iabf-bg-soft, #eef0eb);
  color: var(--iabf-ink-muted, #5c5a63);
}
.meta-chip.is-ok {
  background: rgba(95, 122, 85, 0.15);
  color: var(--iabf-success, #5f7a55);
}
.meta-chip.is-warn {
  background: rgba(196, 146, 58, 0.18);
  color: #8a6a28;
}
.meta-chip.is-pending {
  background: rgba(196, 146, 58, 0.2);
  color: #8a6a28;
}
.pending-note {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(196, 146, 58, 0.12);
  border: 1px solid rgba(196, 146, 58, 0.35);
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink, #1c1b1f);
}
.comp-pill {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--iabf-secondary-soft, #e8e5f0);
  color: var(--iabf-ink, #1c1b1f);
}
.comp-pill--pending {
  background: var(--iabf-bg-soft, #eef0eb);
  color: var(--iabf-ink-muted, #5c5a63);
}

/* Hub collapsible panels */
.hub-panel {
  padding: 0;
  overflow: hidden;
}
.hub-panel-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.hub-panel-toggle:hover {
  background: rgba(238, 240, 235, 0.4);
}
.hub-panel-kicker {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--iabf-primary, #8a957d);
  margin-bottom: 2px;
}
.hub-panel-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  margin: 0 0 4px;
  color: var(--iabf-ink, #1c1b1f);
}
.hub-panel-summary {
  margin: 0;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.4;
}
.hub-panel-body {
  padding: 0 18px 18px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  padding-top: 14px;
}

/* Competition panel */
.comp-intro {
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 14px;
}
.comp-empty {
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.comp-registered {
  background: var(--iabf-bg-soft, #eef0eb);
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.comp-registered-badge {
  font-weight: 700;
  font-size: 14px;
  color: var(--iabf-success, #5f7a55);
  margin-bottom: 4px;
}
.comp-piece-line {
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
}
.comp-division {
  color: var(--iabf-ink-muted, #5c5a63);
}
.comp-division.pending {
  font-style: italic;
}
.comp-survey-warn {
  margin-top: 8px;
  font-size: 14px;
  color: #8a6a28;
  font-weight: 600;
}
.comp-change-label {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
  color: var(--iabf-ink, #1c1b1f);
}
.comp-radio-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}
.comp-radio {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  cursor: pointer;
  background: #fff;
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
}
.comp-radio.selected {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.08);
}
.comp-radio input {
  margin-top: 3px;
}
.comp-radio-sub {
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 400;
}
.comp-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
}
/* router-link renders as <a>; global a { color } can wash out primary btn text */
.comp-cta-link {
  text-decoration: none !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff !important;
}
.comp-cta-link:hover,
.comp-cta-link:focus,
.comp-cta-link:visited {
  color: #fff !important;
  text-decoration: none !important;
}
.comp-declined {
  margin-top: 4px;
}
.comp-declined-status {
  margin: 0 0 10px;
  font-size: 15px;
  line-height: 1.45;
  color: var(--iabf-ink, #1c1b1f);
}
.comp-declined-sub {
  font-weight: 400;
  color: var(--iabf-ink-muted, #5c5a63);
}
.comp-secondary-link {
  font-size: 14px;
  font-weight: 600;
  color: var(--iabf-primary, #8a957d);
  text-decoration: none;
}
.comp-secondary-link:hover {
  text-decoration: underline;
}
.comp-hint {
  margin: 0;
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
}
.comp-about-details {
  margin: 0 0 18px;
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  padding: 12px 16px;
}
.comp-about-details summary {
  cursor: pointer;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 17px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  user-select: none;
}
.comp-about-details summary::-webkit-details-marker {
  display: none;
}
.comp-about-details summary::after {
  content: '▾';
  font-size: 14px;
  line-height: 1;
  color: var(--iabf-primary, #8a957d);
  transition: transform 0.15s ease;
  flex-shrink: 0;
}
.comp-about-details[open] summary {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}
.comp-about-details[open] summary::after {
  transform: rotate(180deg);
}

/* Add wizard */
.wizard-steps {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.wizard-step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  background: var(--iabf-bg-soft, #eef0eb);
  color: var(--iabf-ink-faint, #8a8792);
}
.wizard-step-dot.active {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}
.wizard-step-dot.done {
  background: rgba(138, 149, 125, 0.35);
  color: var(--iabf-ink, #1c1b1f);
}
.wizard-pane-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 20px;
  margin: 0 0 8px;
  padding: 0;
  color: var(--iabf-ink, #1c1b1f);
}
.wizard-pane-lede {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.wizard-tips {
  margin: 0 0 16px;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.wizard-tips a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
}
.wizard-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
.wizard-back {
  border: none;
  background: none;
  color: var(--iabf-ink-muted, #5c5a63);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0;
  margin-top: 0;
}
.wizard-music-note {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 8px 0 0;
}
.wizard-review-note {
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 10px 0 0;
  padding: 10px 12px;
  background: var(--iabf-bg-soft, #eef0eb);
  border-radius: 10px;
}

.lineup-inline {
  background: var(--iabf-bg-soft, #eef0eb);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 8px;
}
.lineup-inline-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
}
.lineup-search {
  margin-bottom: 10px;
}
.lineup-mini-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 220px;
  overflow: auto;
}
.lineup-mini-list li {
  font-size: 14px;
  line-height: 1.4;
  padding: 6px 0;
  border-bottom: 1px solid rgba(227, 224, 232, 0.8);
  color: var(--iabf-ink, #1c1b1f);
}
.lineup-mini-meta {
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 400;
}
.lineup-empty,
.lineup-prompt,
.lineup-more,
.lineup-loading,
.lineup-note {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0;
}
.lineup-more {
  margin-top: 8px;
}

/* How it works */
.how-body p,
.how-body li {
  font-size: 15px;
  line-height: 1.55;
  color: var(--iabf-ink-muted, #5c5a63);
}
.how-h {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 17px;
  margin: 16px 0 8px;
  padding: 0;
  color: var(--iabf-ink, #1c1b1f);
}
.how-h:first-child {
  margin-top: 0;
}
.how-note {
  font-style: italic;
  font-size: 14px !important;
}
.how-video-link {
  margin: 12px 0 4px !important;
  font-size: 15px !important;
}
.how-video-link a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 700;
  text-decoration: none;
}
.how-video-link a:hover {
  text-decoration: underline;
}
.how-video-meta {
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 400;
}
.how-body a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
}

.form-question {
  padding-top: 10px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 16px;
  letter-spacing: -.02em;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0;
}
.form-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 15px;
  margin: 12px 0 8px;
  line-height: 1.4;
}
.group-split-hint {
  margin: 8px 0 4px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
}
.piece-type-hint {
  margin: 8px 0 10px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
  background: var(--iabf-bg-soft, #eef0eb);
  border-radius: 10px;
}
.piece-type-hint p {
  margin: 0 0 8px;
}
.piece-type-hint p:last-child {
  margin-bottom: 0;
}
.piece-type-hint em {
  font-style: italic;
}
.feeling-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 14px;
}
.feeling-chip {
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  background: #fff;
  border-radius: 999px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: var(--iabf-ink-muted, #5c5a63);
  cursor: pointer;
  margin-top: 0;
  text-align: left;
  line-height: 1.3;
}
.feeling-chip.selected {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.12);
  color: var(--iabf-ink, #1c1b1f);
}
.feeling-chip:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.upload-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 12px;
  margin-top: 4px;
  max-width: 240px;
}

.deadline-notice {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 20px 22px;
  margin: 0 0 16px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  line-height: 1.5;
  color: var(--iabf-ink, #1c1b1f);
}
.deadline-notice strong {
  display: block;
  margin-bottom: 6px;
  font-weight: 700;
}
.deadline-notice a {
  color: var(--iabf-primary, #8a957d);
}

.video-embed {
  margin: 12px 0 16px;
}
.video-embed iframe {
  border-radius: 8px;
}
.video-embed-vertical {
  max-width: 320px;
}
.video-embed-vertical iframe {
  width: 100%;
  aspect-ratio: 9 / 16;
  height: auto;
}

/* Prefer project button classes if present; fall back */
.iabf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  text-decoration: none;
  margin-top: 0;
}
.iabf-btn--primary {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}
.iabf-btn--primary:hover {
  filter: brightness(0.96);
}
.iabf-btn--secondary {
  background: #fff;
  color: var(--iabf-primary, #8a957d);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
}
.iabf-btn--secondary:hover {
  background: var(--iabf-bg-soft, #eef0eb);
}
.iabf-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
